import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  MapPin, Plus, Edit, Trash2, GripVertical, Eye, EyeOff, 
  FileText, Loader2, AlertCircle, Globe, Image as ImageIcon, Map
} from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Liste des pays courants
const COUNTRIES = [
  "France", "Espagne", "Italie", "Grèce", "Croatie", "Turquie", "Portugal",
  "Maroc", "Tunisie", "Égypte", "Malte", "Monaco", "Chypre",
  "États-Unis", "Mexique", "Cuba", "République Dominicaine", "Bahamas", "Jamaïque",
  "Brésil", "Argentine", "Chili", "Pérou", "Colombie",
  "Thaïlande", "Vietnam", "Indonésie", "Japon", "Chine", "Inde",
  "Australie", "Nouvelle-Zélande", "Fidji",
  "Émirats Arabes Unis", "Oman", "Jordanie", "Israël",
  "Afrique du Sud", "Kenya", "Madagascar", "Maurice", "Seychelles",
  "Norvège", "Suède", "Islande", "Royaume-Uni", "Irlande", "Allemagne",
  "Autre"
];

// Régions géographiques
const REGIONS = [
  { value: "mediterranee", label: "Méditerranée" },
  { value: "caraibes", label: "Caraïbes" },
  { value: "europe-nord", label: "Europe du Nord" },
  { value: "asie", label: "Asie" },
  { value: "ameriques", label: "Amériques" },
  { value: "oceanie", label: "Océanie" },
  { value: "afrique", label: "Afrique" },
  { value: "moyen-orient", label: "Moyen-Orient" },
  { value: "autre", label: "Autre" },
];

export default function DestinationsManagementPage() {
  const { token } = useAuth();
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingDestination, setEditingDestination] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    name: "",
    slug: "",
    country: "",
    region: "",
    description: "",
    image_url: "",
    latitude: "",
    longitude: "",
    order: 0,
    is_active: true,
  });

  // Geocode state (Bloc P1)
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeResults, setGeocodeResults] = useState([]);

  useEffect(() => {
    loadDestinations();
  }, []);

  const loadDestinations = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/destinations`);
      setDestinations(res.data);
    } catch (error) {
      console.error("Error loading destinations:", error);
      toast.error("Impossible de charger les destinations");
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const handleNameChange = (name) => {
    setForm({
      ...form,
      name,
      slug: editingDestination ? form.slug : generateSlug(name),
    });
  };

  const openCreateModal = () => {
    setEditingDestination(null);
    setGeocodeResults([]);
    setForm({
      name: "",
      slug: "",
      country: "",
      region: "",
      description: "",
      image_url: "",
      latitude: "",
      longitude: "",
      order: destinations.length,
      is_active: true,
    });
    setShowModal(true);
  };

  const openEditModal = (destination) => {
    setEditingDestination(destination);
    setGeocodeResults([]);
    setForm({
      name: destination.name,
      slug: destination.slug,
      country: destination.country || "",
      region: destination.region || "",
      description: destination.description || "",
      image_url: destination.image_url || "",
      latitude: destination.latitude || "",
      longitude: destination.longitude || "",
      order: destination.order || 0,
      is_active: destination.is_active !== false,
    });
    setShowModal(true);
  };

  // Geocode auto : POST /api/geocode?q=name + country + region (Bloc P1)
  const handleGeocode = async () => {
    if (!form.name.trim()) {
      toast.error("Renseignez d'abord le nom de la destination");
      return;
    }
    const parts = [form.name.trim(), form.region, form.country].filter(Boolean);
    const q = parts.join(", ");
    setGeocoding(true);
    setGeocodeResults([]);
    try {
      const res = await axios.get(`${API}/api/geocode`, { params: { q } });
      const results = res.data || [];
      if (results.length === 0) {
        toast.error("Aucun résultat trouvé");
      } else if (results.length === 1) {
        // Un seul résultat : applique directement en création, confirme en édition si lat/lng deja remplies
        const hasCoords = form.latitude !== "" && form.longitude !== "";
        if (editingDestination && hasCoords) {
          setGeocodeResults(results);
          toast.info("Résultat trouvé — cliquez pour écraser les coordonnées existantes");
        } else {
          applyGeocodeResult(results[0]);
        }
      } else {
        setGeocodeResults(results);
        toast.info(`${results.length} résultats — sélectionnez le bon`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur géocodage");
    } finally {
      setGeocoding(false);
    }
  };

  // Applique le résultat : n'écrase que si vide OU si l'utilisateur clique explicitement (dropdown = clic explicite)
  const applyGeocodeResult = (result) => {
    setForm(prev => ({
      ...prev,
      latitude: String(result.lat),
      longitude: String(result.lng),
    }));
    setGeocodeResults([]);
    toast.success(`Coordonnées appliquées : ${result.label}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name.trim()) {
      toast.error("Le nom de la destination est obligatoire");
      return;
    }

    if (!form.slug.trim()) {
      toast.error("L'identifiant (slug) est obligatoire");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...form,
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
      };

      if (editingDestination) {
        // Update
        await axios.put(
          `${API}/api/destinations/${editingDestination.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Destination modifiée");
      } else {
        // Create
        await axios.post(
          `${API}/api/destinations`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Destination créée");
      }
      
      setShowModal(false);
      loadDestinations();
    } catch (error) {
      console.error("Error saving destination:", error);
      const message = error.response?.data?.detail || "Erreur lors de l'enregistrement";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (destination) => {
    try {
      await axios.delete(
        `${API}/api/destinations/${destination.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Destination supprimée");
      setDeleteConfirm(null);
      loadDestinations();
    } catch (error) {
      console.error("Error deleting destination:", error);
      const message = error.response?.data?.detail || "Impossible de supprimer la destination";
      toast.error(message);
    }
  };

  const toggleActive = async (destination) => {
    try {
      await axios.put(
        `${API}/api/destinations/${destination.id}`,
        { ...destination, is_active: !destination.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      loadDestinations();
      toast.success(destination.is_active ? "Destination désactivée" : "Destination activée");
    } catch (error) {
      console.error("Error toggling destination:", error);
      toast.error("Erreur lors de la modification");
    }
  };

  const getRegionLabel = (value) => {
    const region = REGIONS.find(r => r.value === value);
    return region ? region.label : value;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="destinations-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-primary flex items-center gap-2">
            <MapPin className="h-6 w-6 text-accent" />
            Destinations
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Gérez les destinations de voyage
          </p>
        </div>
        <Button onClick={openCreateModal} className="bg-accent hover:bg-accent/90">
          <Plus className="h-4 w-4 mr-2" />
          Nouvelle destination
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-primary">{destinations.length}</div>
            <p className="text-xs text-muted-foreground">Total</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">
              {destinations.filter(d => d.is_active !== false).length}
            </div>
            <p className="text-xs text-muted-foreground">Actives</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-accent">
              {new Set(destinations.map(d => d.country).filter(Boolean)).size}
            </div>
            <p className="text-xs text-muted-foreground">Pays</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-accent">
              {destinations.reduce((sum, d) => sum + (d.article_count || 0), 0)}
            </div>
            <p className="text-xs text-muted-foreground">Articles</p>
          </CardContent>
        </Card>
      </div>

      {/* Destinations List */}
      {destinations.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <MapPin className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">Aucune destination créée</p>
            <Button onClick={openCreateModal} variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Créer la première destination
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {destinations.map((destination) => (
            <Card 
              key={destination.id}
              className={`overflow-hidden transition-all hover:shadow-lg ${
                destination.is_active === false ? "opacity-60" : ""
              }`}
            >
              {/* Image */}
              <div className="aspect-video bg-secondary relative overflow-hidden">
                {destination.image_url ? (
                  <img 
                    src={destination.image_url} 
                    alt={destination.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Map className="h-12 w-12 text-muted-foreground/30" />
                  </div>
                )}
                {destination.is_active === false && (
                  <Badge className="absolute top-2 right-2" variant="secondary">
                    Inactive
                  </Badge>
                )}
              </div>

              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-primary truncate">{destination.name}</h3>
                    <p className="text-xs text-muted-foreground">
                      {destination.country && <span>{destination.country}</span>}
                      {destination.region && (
                        <span className="ml-1">• {getRegionLabel(destination.region)}</span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      /{destination.slug}
                      {destination.article_count > 0 && (
                        <span className="ml-2">• {destination.article_count} article{destination.article_count > 1 ? "s" : ""}</span>
                      )}
                    </p>
                  </div>
                </div>

                {destination.description && (
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                    {destination.description}
                  </p>
                )}

                <div className="flex items-center justify-end gap-1 mt-4 pt-4 border-t">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleActive(destination)}
                    title={destination.is_active !== false ? "Désactiver" : "Activer"}
                  >
                    {destination.is_active !== false ? (
                      <Eye className="h-4 w-4 text-green-600" />
                    ) : (
                      <EyeOff className="h-4 w-4 text-gray-400" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEditModal(destination)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setDeleteConfirm(destination)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingDestination ? "Modifier la destination" : "Nouvelle destination"}
            </DialogTitle>
            <DialogDescription>
              {editingDestination 
                ? "Modifiez les informations de la destination"
                : "Ajoutez une nouvelle destination de voyage"
              }
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Ex: Barcelone"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">Identifiant (slug) *</Label>
              <Input
                id="slug"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                placeholder="Ex: barcelone"
                required
              />
              <p className="text-xs text-muted-foreground">
                Utilisé dans l'URL : /destination/{form.slug || "..."}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Pays</Label>
                <Select 
                  value={form.country} 
                  onValueChange={(value) => setForm({ ...form, country: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRIES.map((country) => (
                      <SelectItem key={country} value={country}>
                        {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Région</Label>
                <Select 
                  value={form.region} 
                  onValueChange={(value) => setForm({ ...form, region: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {REGIONS.map((region) => (
                      <SelectItem key={region.value} value={region.value}>
                        {region.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Présentation de la destination..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="image_url">URL de l'image</Label>
              <Input
                id="image_url"
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                placeholder="https://..."
              />
              {form.image_url && (
                <div className="mt-2 aspect-video max-w-xs rounded overflow-hidden bg-secondary">
                  <img 
                    src={form.image_url} 
                    alt="Aperçu"
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="latitude">Latitude</Label>
                <Input
                  id="latitude"
                  type="number"
                  step="any"
                  value={form.latitude}
                  onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                  placeholder="Ex: 41.3851"
                  data-testid="destination-input-latitude"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="longitude">Longitude</Label>
                <Input
                  id="longitude"
                  type="number"
                  step="any"
                  value={form.longitude}
                  onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                  placeholder="Ex: 2.1734"
                  data-testid="destination-input-longitude"
                />
              </div>
            </div>

            {/* Geocode: bouton "Localiser automatiquement" (Bloc P1) */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleGeocode}
                  disabled={geocoding || !form.name.trim()}
                  data-testid="destination-btn-geocode"
                >
                  {geocoding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <MapPin className="h-4 w-4 mr-2" />}
                  Localiser automatiquement
                </Button>
                <span className="text-xs text-muted-foreground">
                  {form.latitude && form.longitude
                    ? "Les coordonnées seront écrasées si vous cliquez et sélectionnez un résultat."
                    : "Renseigne lat/lng à partir du nom + pays."}
                </span>
              </div>
              {geocodeResults.length > 0 && (
                <div className="border border-border rounded-md bg-white shadow-sm max-h-48 overflow-y-auto" data-testid="destination-geocode-results">
                  {geocodeResults.map((r, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => applyGeocodeResult(r)}
                      className="w-full text-left px-3 py-2 hover:bg-secondary/50 border-b border-border last:border-b-0 text-sm"
                      data-testid={`destination-geocode-result-${idx}`}
                    >
                      <div className="font-medium text-primary truncate">{r.label}</div>
                      <div className="text-xs text-muted-foreground">
                        {r.lat.toFixed(4)}, {r.lng.toFixed(4)} · {r.source === "destination" ? "Destination existante" : "OpenStreetMap"}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="order">Ordre d'affichage</Label>
                <Input
                  id="order"
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                  min={0}
                />
              </div>

              <div className="space-y-2">
                <Label>Statut</Label>
                <div className="flex items-center gap-2 pt-2">
                  <Switch
                    checked={form.is_active}
                    onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                  />
                  <span className="text-sm text-muted-foreground">
                    {form.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={saving} className="bg-accent hover:bg-accent/90">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingDestination ? "Enregistrer" : "Créer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Confirmer la suppression
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer la destination "{deleteConfirm?.name}" ?
              {deleteConfirm?.article_count > 0 && (
                <span className="block mt-2 text-amber-600">
                  ⚠️ Cette destination est associée à {deleteConfirm.article_count} article(s).
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Annuler
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => handleDelete(deleteConfirm)}
            >
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
